import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';
import { getAssociationById, getAssociationFeeById, updateAssociationFee } from 'src/shared/services/associationService';
import { getOrganizationFee } from 'src/shared/services/organizationService';
import { getOrganizationAssociationId } from 'src/shared/functions/organizationHelpers';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { getPaymentMethodOptions, PaymentMethodEnum } from 'src/shared/models/enums/paymentMethod.enum';
import { Add } from '@mui/icons-material';
import { AssociationSendingMethodHandler } from '../components/associationSendingMethodHandler';
import { AssociationFeeProfileHandler } from '../components/associationFeeProfileHandler';
import { AssociationFeeRequestSchema } from '../schemas/associationFeeRequestSchema';
import { AssociationFeeRequest } from '../models/associationFeeRequest';
import { AssociationFee, PaymentMethodOption } from '../models/associationFee';

export const AssociationFeePage = () => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const params = useParams();
  const isMounted = useIsMounted();

  const readOnlyMode = false;

  const currentAssocationId = getOrganizationAssociationId(currentUser, params.id);
  const paymentMethodOptions = getPaymentMethodOptions(t);

  const [association, setAssociation] = useState<AssociationFee>();
  const [associationOcr, setAssociationOcr] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [openAddprofile, setOpenAddprofile] = useState<boolean>(false);

  const schema = AssociationFeeRequestSchema(t);
  const formMethods = useForm<AssociationFeeRequest>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      localAss_id: Number(currentAssocationId) || 0,
      paymentMethod: 0,
      ocr: 'false',
      sameFeeForAll: 0,
      mainAndFamily: {
        feeMainMember: 0,
        feeFamilyMember: 0,
        feeMainMemberAutogiro: 0,
        feeFamilyMemberAutogiro: 0,
      },
      familySteps: [
        { member: 'mainMember', fee: 0 },
        { member: 'familyMember1', fee: 0 },
      ],
      oneFeeForHousehold: 0,
      feeBasedOnProfile: undefined,
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = formMethods;
  ConsoleHelper.log('Form validation errors:', errors);

  const paymentMethod = watch('paymentMethod');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [feeResult, assResult] = await Promise.all([getAssociationFeeById(currentAssocationId), getAssociationById(currentAssocationId)]);

      if (isMounted()) {
        setAssociation(feeResult?.data);
        setAssociationOcr(assResult?.data?.ocr === true);

        // Om centralConfig, visa förbundets avgifter istället
        let feeData = feeResult.data;
        if (feeResult.data.centralConfig) {
          try {
            const orgFeeResult = await getOrganizationFee();
            feeData = orgFeeResult.data;
          } catch {
            // Fallback till föreningens egna data
          }
        }

        if (feeData.paymentMethod) {
          setValue('paymentMethod', feeData.paymentMethod);
          setValue('feeBasedOnProfile', feeData.feeBasedOnProfile);
          if (feeData.mainAndFamily !== null) setValue('mainAndFamily', feeData.mainAndFamily);
          if (feeData.familySteps !== null) setValue('familySteps', feeData.familySteps);
          setValue('sameFeeForAll', feeData.sameFeeForAll);
          setValue('sameFeeForAllAutogiro', feeData.sameFeeForAllAutogiro ?? undefined);
          setValue('oneFeeForHousehold', feeData.oneFeeForHousehold);
          setValue('oneFeeForHouseholdAutogiro', feeData.oneFeeForHouseholdAutogiro ?? undefined);
          setValue('ocr', feeData.ocr?.toString() ?? 'false');
        }
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [currentAssocationId, isMounted, setValue]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectedPaymentMethod = paymentMethodOptions.find((opt) => opt.value === watch('paymentMethod'));

  const FieldsToClean: Record<PaymentMethodEnum, Array<keyof AssociationFeeRequest>> = {
    [PaymentMethodEnum.SameFeeForAll]: ['mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.MainAndFamily]: ['sameFeeForAll', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.FamilySteps]: ['sameFeeForAll', 'mainAndFamily', 'oneFeeForHousehold'],
    [PaymentMethodEnum.OneFeeForHousehold]: ['sameFeeForAll', 'mainAndFamily', 'familySteps'],
    [PaymentMethodEnum.FeeBasedOnProfile]: ['sameFeeForAll', 'mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
    [PaymentMethodEnum.None]: ['sameFeeForAll', 'mainAndFamily', 'familySteps', 'oneFeeForHousehold'],
  };

  const onSubmit = async (data: AssociationFeeRequest) => {
    setLoading(true);

    const cleanedData: AssociationFeeRequest = {
      ...data,
      ocr: data.ocr === 'true',
    };
    if (typeof paymentMethod !== 'number') return;
    FieldsToClean[paymentMethod as PaymentMethodEnum]?.forEach((field) => {
      (cleanedData as Partial<AssociationFeeRequest>)[field] = undefined;
    });

    try {
      await updateAssociationFee(cleanedData);
      if (isMounted()) {
        reset();
        toast.success(<>{t('association:feeUpdateSuccess')}</>);
        fetchData();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (isMounted()) {
        const errorMessage = e?.data?.messages;
        toast.error(<>{errorMessage}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 2 }}>
            <Grid container>
              {association?.centralConfig && (
                <Grid item xs={12}>
                  <Alert severity='warning'>
                    {t('association:centralNotice')}. {t('association:centralNoticeOnlyFederation')}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} sx={association?.centralConfig ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                <Grid container mt={2}>
                  <Grid item xs={12}>
                    <FormControlLabel control={<Checkbox checked={associationOcr} disabled />} label={t('association:useOcr')} />
                  </Grid>
                </Grid>
                <Grid container mt={1} spacing={2}>
                  <Grid item xs={10} md={5} sx={{ mb: { xs: -5, md: 0 } }}>
                    <AutocompleteDropdown<PaymentMethodOption>
                      options={paymentMethodOptions}
                      disable={readOnlyMode}
                      name='paymentMethod'
                      nameProp='description'
                      placeholder={t('association:paymentMethod.none')}
                      emptyLabel={t('association:paymentMethod.none')}
                      value={selectedPaymentMethod}
                      keyProp='id'
                      label='Aviseringsmetod'
                      callback={(val) => {
                        setValue('paymentMethod', val?.value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={7} sx={{ ml: { xs: 0 }, mt: { xs: 5, md: 0 } }}>
                    <AssociationSendingMethodHandler
                      autogiroFee={currentUser?.autogiroEnabled}
                      readOnlyMode={readOnlyMode}
                      paymentMethod={paymentMethod || 0}
                    />
                  </Grid>
                </Grid>
                {(paymentMethod === PaymentMethodEnum.FeeBasedOnProfile ||
                  (association?.feeBasedOnProfile && association.feeBasedOnProfile.length > 0)) && (
                  <Grid item xs={12} mt={5}>
                    <AssociationFeeProfileHandler
                      autogiroFee={currentUser?.autogiroEnabled}
                      readOnlyMode={readOnlyMode}
                      openAddprofile={openAddprofile}
                      setOpenAddprofile={setOpenAddprofile}
                      paymentMethod={paymentMethod as PaymentMethodEnum}
                    />
                  </Grid>
                )}
                {!readOnlyMode && (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'space-between', sm: 'flex-end' }, mt: 2 }}>
                    <Button
                      variant='outlined'
                      size='small'
                      disableElevation
                      onClick={() => {
                        setOpenAddprofile(true);
                      }}
                    >
                      <Add fontSize='small' />
                      {t('association:addProfile')}
                    </Button>

                    <Button
                      disabled={paymentMethod === undefined || paymentMethod === 0}
                      sx={{ marginLeft: 2 }}
                      type='submit'
                      variant='contained'
                      disableElevation
                    >
                      {t('common:button.save')}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Box>
        </form>
      </FormProvider>
    </FormLoaderWrapper>
  );
};
